import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { CreditCard, AlertTriangle, CheckCircle2, XCircle, Check, Wallet } from "lucide-react";
import { toast } from "sonner";
import { CustomPlanAbbonamento } from "@/components/CustomPlanAbbonamento";

function formatEUR(v) {
  return Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

function formatDateIT(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("it-IT", { timeZone: "Europe/Rome" }); }
  catch { return iso; }
}

const STATUS_LABEL = {
  trialing: { label: "Prova gratuita", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  active: { label: "Attivo", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  past_due: { label: "Pagamento in sospeso", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  canceled: { label: "Annullato", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  expired: { label: "Scaduto", cls: "bg-red-100 text-red-700 border-red-200" },
  pending: { label: "In attesa", cls: "bg-slate-100 text-slate-700 border-slate-200" },
};

export default function Abbonamento() {
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [changing, setChanging] = useState(null); // plan id being switched to

  const load = async () => {
    try {
      const [pl, cur, m] = await Promise.all([
        api.get("/billing/plans"),
        api.get("/billing/plan"),
        api.get("/auth/me"),
      ]);
      setPlans(pl.data.plans || []);
      setCurrentPlan(cur.data);
      setMe(m.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  useEffect(() => { load(); }, []);

  const subscribe = async (planId) => {
    setLoading(true);
    try {
      const res = await api.post("/billing/subscribe", { origin: window.location.origin, plan_id: planId });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading(false);
    }
  };

  const changePlan = async (planId) => {
    setChanging(planId);
    try {
      await api.post("/billing/change-plan", { plan_id: planId });
      toast.success(`Piano aggiornato a ${planId}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setChanging(null);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Vuoi annullare il rinnovo automatico? Manterrai l'accesso fino alla fine del periodo già pagato.")) return;
    try {
      await api.post("/billing/cancel");
      toast.success("Rinnovo annullato");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleAddon = async (field, value) => {
    try {
      await api.put("/agency", { [field]: value });
      toast.success(value ? "Add-on attivato" : "Add-on disattivato");
      window.dispatchEvent(new CustomEvent("agency:updated"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resume = async () => {
    try {
      await api.post("/billing/resume");
      toast.success("Abbonamento riattivato");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!currentPlan || !me) return <div className="text-slate-500 text-sm">Caricamento…</div>;

  const ag = me.agency || {};
  if (currentPlan.is_custom) return <CustomPlanAbbonamento plan={currentPlan} ag={ag} />;
  const status = STATUS_LABEL[ag.plan_status] || STATUS_LABEL.pending;
  const isTrialing = ag.plan_status === "trialing";
  const planIncludesAgentFlow = !!currentPlan.plan_includes_agent_flow;
  const planIncludesCollabFlow = !!currentPlan.plan_includes_collab_flow_module;
  const planIncludesPlurimandatari = !!currentPlan.plan_includes_plurimandatari;

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Pagamenti</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Abbonamento</h1>
        <p className="text-sm text-slate-500 mt-1">Gestisci il piano della tua agenzia. Puoi cambiare piano in qualsiasi momento.</p>
      </div>

      {/* Piano corrente */}
      <Card className="p-6 border-slate-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-blue-600 font-semibold">Piano corrente</div>
            <h2 className="font-heading text-2xl font-bold text-slate-900 mt-1" data-testid="current-plan-name">{currentPlan.plan_name}</h2>
            <div className="text-sm text-slate-600 mt-1">
              <span className="font-semibold">{formatEUR(currentPlan.price_net)}/mese</span>
              <span className="text-xs text-slate-500 ml-2">compresi oneri di legge</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {currentPlan.current_extra_users}{currentPlan.max_extra_users !== null ? ` / ${currentPlan.max_extra_users}` : ""} collaboratori{currentPlan.max_extra_users === null ? " (illimitati)" : ""}
              {currentPlan.retention_label && (
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-[11px] font-medium">
                  Storico {currentPlan.retention_label}
                </span>
              )}
            </div>
          </div>
          <Badge variant="outline" className={`${status.cls} rounded-full px-3 py-1`} data-testid="plan-status">{status.label}</Badge>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          <div className="border border-slate-200 rounded-lg p-4">
            <div className="text-xs text-slate-500 uppercase font-semibold">Prossima scadenza</div>
            <div className="font-heading text-lg font-semibold mt-1">{formatDateIT(ag.current_period_end)}</div>
          </div>
          {ag.trial_ends_at && (
            <div className="border border-slate-200 rounded-lg p-4">
              <div className="text-xs text-slate-500 uppercase font-semibold">Fine periodo di prova</div>
              <div className="font-heading text-lg font-semibold mt-1">{formatDateIT(ag.trial_ends_at)}</div>
            </div>
          )}
        </div>

        {ag.cancel_at_period_end && (
          <div className="mt-5 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5" />
            Il rinnovo automatico è annullato. L'accesso resta attivo fino al {formatDateIT(ag.current_period_end)}.
          </div>
        )}

        <div className="flex flex-wrap gap-2 mt-6">
          {(ag.plan_status === "trialing" || ag.plan_status === "active" || ag.plan_status === "past_due" || ag.plan_status === "expired") && (
            <Button onClick={() => subscribe(currentPlan.plan_id)} disabled={loading} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="subscribe-btn">
              <CreditCard className="w-4 h-4 mr-2" />
              {loading ? "Reindirizzamento…" : (ag.plan_status === "active" ? `Rinnova ora · ${formatEUR(currentPlan.price_total)}` : `Attiva ora · ${formatEUR(currentPlan.price_total)}`)}
            </Button>
          )}
          {ag.plan_status === "active" && !ag.cancel_at_period_end && (
            <Button variant="outline" onClick={cancel} data-testid="cancel-btn"><XCircle className="w-4 h-4 mr-2" /> Annulla rinnovo</Button>
          )}
          {ag.plan_status === "active" && ag.cancel_at_period_end && (
            <Button variant="outline" onClick={resume} data-testid="resume-btn"><CheckCircle2 className="w-4 h-4 mr-2" /> Riattiva</Button>
          )}
        </div>
      </Card>

      {/* Servizi inclusi nel piano */}
      <Card className="p-6 border-slate-200">
        <div className="flex items-center gap-2 mb-1">
          <Wallet className="w-5 h-5 text-emerald-600" />
          <h3 className="font-heading text-lg font-semibold text-slate-900">Servizi inclusi nel piano</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">Tutte queste funzionalità sono incluse nel tuo piano. Puoi scegliere se attivarle o lasciarle disattive.</p>

        <div className="space-y-3">
          <div className={`border border-slate-200 rounded-lg p-4 flex items-start justify-between gap-3 ${planIncludesAgentFlow ? "bg-emerald-50/40 border-emerald-200" : ""}`}>
            <div className="flex-1">
              <div className="font-semibold text-slate-900">
                Flusso di cassa agente{" "}
                {planIncludesAgentFlow ? (
                  <span className="text-xs text-emerald-700 font-semibold ml-2">✓ Incluso nel piano {currentPlan.plan_name}</span>
                ) : (
                  <span className="text-xs text-slate-500 ml-2">+{formatEUR(currentPlan.addons?.agent_flow_price_net || 4.99)} / mese</span>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-1">Traccia la liquidità (saldo iniziale, incassi, versamenti, uscite) sul foglio cassa di agenzia.</div>
            </div>
            <Switch
              data-testid="addon-agent-flow"
              checked={!!(currentPlan.addons?.agent_flow)}
              onCheckedChange={(v) => toggleAddon("flusso_cassa_agente", v)}
            />
          </div>

          <div className={`border border-slate-200 rounded-lg p-4 flex items-start justify-between gap-3 ${planIncludesCollabFlow ? "bg-emerald-50/40 border-emerald-200" : ""}`}>
            <div className="flex-1">
              <div className="font-semibold text-slate-900">
                Modulo flusso di cassa per collaboratori{" "}
                {planIncludesCollabFlow ? (
                  <span className="text-xs text-emerald-700 font-semibold ml-2">✓ Incluso nel piano {currentPlan.plan_name}</span>
                ) : (
                  <span className="text-xs text-slate-500 ml-2">+{formatEUR(currentPlan.addons?.collab_flow_price_net || 4.99)} / mese</span>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-1">Sblocca il toggle "Flusso di cassa" sui singoli collaboratori (gestibile dalla pagina Utenze).</div>
            </div>
            <Switch
              data-testid="addon-collab-flow-module"
              checked={!!(currentPlan.addons?.collab_flow_module)}
              onCheckedChange={(v) => toggleAddon("flusso_cassa_collab_modulo", v)}
            />
          </div>

          <div className={`border border-slate-200 rounded-lg p-4 flex items-start justify-between gap-3 ${planIncludesPlurimandatari ? "bg-emerald-50/40 border-emerald-200" : "opacity-90"}`}>
            <div className="flex-1">
              <div className="font-semibold text-slate-900">
                Plurimandatari (multi-compagnia){" "}
                {planIncludesPlurimandatari ? (
                  <span className="text-xs text-emerald-700 font-semibold ml-2">✓ Incluso nel piano {currentPlan.plan_name}</span>
                ) : (
                  <span className="text-xs text-amber-700 font-semibold ml-2 inline-flex items-center gap-1">
                    🔒 Disponibile dal piano Pro
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Aggiunge una colonna "Compagnia" sul foglio cassa. Gestisci l'elenco delle compagnie da Impostazioni → Compagnie. Puoi scegliere se inviare a fine giornata un riepilogo unico o un riepilogo separato per ciascuna compagnia.
              </div>
              {!planIncludesPlurimandatari && (
                <a
                  href="#all-plans"
                  className="text-xs text-blue-700 hover:text-blue-900 font-semibold mt-2 inline-block"
                  data-testid="plurimandatari-upgrade-link"
                  onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="plans-grid"]')?.scrollIntoView({ behavior: "smooth" }); }}
                >
                  → Passa al piano Pro o Unlimited per attivare
                </a>
              )}
            </div>
            <Switch
              data-testid="addon-plurimandatari"
              checked={!!(currentPlan.addons?.plurimandatari)}
              onCheckedChange={(v) => toggleAddon("plurimandatari_abilitato", v)}
              disabled={!planIncludesPlurimandatari}
            />
          </div>

          {currentPlan.pricing && currentPlan.pricing.breakdown && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="pricing-breakdown">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Riepilogo costo mensile</div>
              <div className="text-sm space-y-1">
                {currentPlan.pricing.breakdown.map((b, i) => (
                  <div key={`${b.label || "row"}-${i}`} className="flex justify-between">
                    <span className="text-slate-700">{b.label}{b.included && <span className="ml-2 text-xs text-emerald-700 font-semibold">(incluso)</span>}</span>
                    <span className="font-mono-num font-medium">{b.included ? "incluso" : formatEUR(b.amount_net)}</span>
                  </div>
                ))}
                <div className="border-t border-slate-200 pt-1 mt-1 flex justify-between">
                  <span className="font-semibold text-slate-900">Totale mensile</span>
                  <span className="font-mono-num font-bold text-slate-900">{formatEUR(currentPlan.pricing.total_net)}/mese</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-1 text-right">compresi oneri di legge · regime forfettario</div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Tutti i piani */}
      <div id="all-plans">
        <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3">Tutti i piani disponibili</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="plans-grid">
          {plans.map((p) => {
            const isCurrent = p.id === currentPlan.plan_id;
            return (
              <Card
                key={p.id}
                className={`p-5 border-slate-200 flex flex-col ${isCurrent ? "ring-2 ring-slate-900" : ""}`}
                data-testid={`plan-card-${p.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-heading font-bold text-lg text-slate-900">{p.name}</div>
                  {isCurrent && <Badge className="bg-slate-900 text-white rounded-full text-[10px] px-2 py-0.5">Corrente</Badge>}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{p.tagline}</div>

                <div className="mt-4">
                  <div className="font-heading font-bold text-2xl text-slate-900 font-mono-num">
                    {formatEUR(p.price_net)}
                    <span className="text-xs font-normal text-slate-500 ml-1">/ mese</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">compresi oneri di legge</div>
                </div>

                <ul className="mt-4 space-y-1.5 text-sm text-slate-700 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 mt-0.5 text-emerald-600 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-5">
                  {isCurrent ? (
                    <Button disabled variant="outline" className="w-full" data-testid={`plan-current-${p.id}`}>
                      Piano corrente
                    </Button>
                  ) : isTrialing ? (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => changePlan(p.id)}
                      disabled={changing === p.id}
                      data-testid={`plan-switch-${p.id}`}
                    >
                      {changing === p.id ? "Attendi…" : "Passa a questo piano"}
                    </Button>
                  ) : (
                    <Button
                      className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                      onClick={() => subscribe(p.id)}
                      disabled={loading}
                      data-testid={`plan-subscribe-${p.id}`}
                    >
                      <CreditCard className="w-3.5 h-3.5 mr-1" /> Passa a {p.name}
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
        {isTrialing && (
          <p className="text-xs text-slate-500 mt-3">
            Sei in prova gratuita: cambio piano immediato e senza addebito. Il pagamento verrà richiesto al termine del periodo di prova.
          </p>
        )}
      </div>

      <Card className="p-6 border-slate-200">
        <h3 className="font-heading text-lg font-semibold text-slate-900">Dati di fatturazione</h3>
        <div className="grid sm:grid-cols-2 gap-3 mt-4 text-sm">
          <div><span className="text-slate-500">Agenzia:</span> <span className="font-medium">{ag.nome_agenzia}</span></div>
          <div><span className="text-slate-500">P.IVA:</span> <span className="font-mono">{ag.partita_iva || "—"}</span></div>
          <div><span className="text-slate-500">Codice SDI:</span> <span className="font-mono">{ag.codice_sdi || "—"}</span></div>
          <div><span className="text-slate-500">PEC:</span> {ag.pec || "—"}</div>
          <div className="sm:col-span-2"><span className="text-slate-500">Indirizzo:</span> {ag.indirizzo || "—"}</div>
        </div>
      </Card>
    </div>
  );
}
